import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Menu, X, Globe, LogOut, User } from 'lucide-react';
import { Button } from './ui/button';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_teamcraft-app-2/artifacts/5lqryo0g_logo%20banner%20grey.jpg';

const Navbar = () => {
  const { t, language, toggleLanguage } = useLanguage();
  const { isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/services', label: t('nav.services') },
    { path: '/diagnosis', label: t('nav.diagnosis') },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm" data-testid="navbar">
      <nav className="container-custom py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img src={LOGO_URL} alt="TeamCraft" className="h-14 md:h-16 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                className={`text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-[#1A1A1A] border-b-2 border-[#C5A059]'
                    : 'text-gray-600 hover:text-[#1A1A1A]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side actions */}
          <div className="hidden md:flex items-center gap-4">
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1 text-sm text-gray-600 hover:text-[#1A1A1A] transition-colors"
              data-testid="language-toggle"
            >
              <Globe className="w-4 h-4" />
              {language.toUpperCase()}
            </button>

            {isAuthenticated ? (
              <div className="flex items-center gap-3">
                <Link to="/admin" data-testid="admin-link">
                  <Button variant="ghost" size="sm" className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {t('nav.admin')}
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={logout}
                  className="flex items-center gap-2"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <Link to="/login" data-testid="login-link">
                <Button variant="outline" size="sm">
                  {t('nav.login')}
                </Button>
              </Link>
            )}

            <Link to="/diagnosis" data-testid="cta-diagnosis-btn">
              <Button className="bg-[#1A1A1A] hover:bg-[#333] text-white px-6">
                {t('hero.cta')}
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-gray-100 animate-fade-in" data-testid="mobile-menu">
            <div className="flex flex-col gap-4 pt-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`text-base font-medium py-2 ${
                    isActive(link.path) ? 'text-[#1A1A1A]' : 'text-gray-600'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <button
                  onClick={toggleLanguage}
                  className="flex items-center gap-2 text-gray-600"
                >
                  <Globe className="w-4 h-4" />
                  {language.toUpperCase()}
                </button>
                {isAuthenticated ? (
                  <Button variant="outline" size="sm" onClick={logout}>
                    {t('nav.logout')}
                  </Button>
                ) : (
                  <Link to="/login">
                    <Button variant="outline" size="sm">
                      {t('nav.login')}
                    </Button>
                  </Link>
                )}
              </div>
              <Link to="/diagnosis" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full bg-[#1A1A1A] hover:bg-[#333] text-white mt-2">
                  {t('hero.cta')}
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
